import type { QueryClient } from '@tanstack/react-query';
import { entityChannel } from './channels';
import { sameLocks, toBridgeLocks, toLockResult, type BridgeLocks, type LockResult } from './bridge-locks';
import { createEntityPresence } from './entity-presence';
import type { ActiveRealtime } from './events';
import { createAvatarSource, type AvatarSource } from './peer-avatar';
import type { Peer } from './peers';
import { colorForTab } from './tab-color';

/** One other tab on an entity, as the Design Studio editor sees it. */
export type BridgePeer = {
  clientId: string;
  tab: string;
  user: { id: number; name: string; avatar?: string };
  focus?: { field: string; label?: string };
};

export type { BridgeLockHolder, BridgeLocks, LockResult } from './bridge-locks';

/** This tab's hold on one entity channel, from `joinEntity`. */
export interface HydraEntityPresence {
  /** Other tabs on the entity, including the user's own other tabs. */
  peers(): BridgePeer[];
  /** Called with the full peer list whenever it changes; returns unsubscribe. */
  subscribePeers(listener: (peers: BridgePeer[]) => void): () => void;
  /** Publishes this tab's focus on the entity; null clears it. */
  setFocus(field: string | null, label?: string): void;
  /**
   * Takes the lock on `field` if it is free (`^[A-Za-z0-9:_!.-]{1,128}$`); otherwise resolves with
   * `acquired: false` and the holder. Hydra renews it every 10s while this tab holds it. Rejects
   * when the server cannot be reached. Locks belong to the entity's shared session: joins of the
   * same entity see the same locks, and any of them may `release` one.
   *
   * The request stands until `release(field)` or the last `leave()` of the entity (requests belong to
   * the shared session, like the locks): while the field is free, including
   * after a rejected call or a lock that expired during an outage, hydra asks for it again on each
   * renewal. It ends by itself once another tab holds the field, so a tab never takes back a lock
   * someone else got. Call `release` when the editor stops wanting the field.
   */
  lock(field: string): Promise<LockResult>;
  /**
   * Takes the lock on `field` from whoever holds it. Like `lock`, the request stands until
   * `release` or the last `leave()`; a retry asks for the field only while it is free, never takes
   * it over.
   */
  takeOver(field: string): Promise<LockResult>;
  /** Frees `field` if this tab holds it. Resolves even when the server cannot be reached. */
  release(field: string): Promise<void>;
  /** Every lock on the entity by field name, as the server last said. The same map until a lock changes. */
  locks(): BridgeLocks;
  /** Called with the full lock table whenever it changes; returns unsubscribe. */
  subscribeLocks(listener: (locks: BridgeLocks) => void): () => void;
  /**
   * Clears the focus this handle set and drops its hold; the last leave unsubscribes and frees
   * every lock this tab holds on the entity. Idempotent.
   */
  leave(): void;
}

/** `window.__hydra__.realtime`: hydra provides it while connected to realtime, the editor consumes it. */
export interface HydraRealtimeBridge {
  version: 1;
  /** This tab's id. */
  selfTab: string;
  /** The colors everyone sees `tab` in, as resolved CSS values (never `var(...)`). */
  colorForTab(tab: string): { pointer: string; label: string };
  /**
   * Joins the channel of one entity (`type` matches `^[a-z_]{1,32}$`, `id` matches
   * `^[A-Za-z0-9_-]{1,64}$`), so focus follows the entity rather than the URL. Joins of the same
   * entity share one subscription; call `leave()` when done. An invalid type or id returns an
   * inert handle with no peers.
   */
  joinEntity(type: string, id: string): HydraEntityPresence;
}

const CSS_VAR = /^var\(\s*(--[^,)\s]+)\s*(?:,\s*(.+))?\)$/;

/** `var(--pluma-x)` becomes the color it currently resolves to, so a non-Pluma app can paint with it. */
function resolveCssValue(value: string): string {
  const match = CSS_VAR.exec(value.trim());
  if (match === null) return value;
  const resolved = getComputedStyle(document.documentElement)
    .getPropertyValue(match[1] ?? '')
    .trim();
  return resolved !== '' ? resolved : (match[2]?.trim() ?? value);
}

function toBridgePeer({ clientId, tab, user, focus }: Peer, avatars: AvatarSource): BridgePeer {
  const peer: BridgePeer = { clientId, tab, user: { id: user.id, name: user.name } };
  const avatar = avatars.lookup(user.id) ?? user.avatar;
  if (avatar !== undefined) peer.user.avatar = avatar;
  if (focus?.field != null) {
    peer.focus = focus.label === undefined ? { field: focus.field } : { field: focus.field, label: focus.label };
  }
  return peer;
}

/** Everything a bridge listener can see about a peer, so an unchanged list isn't re-sent. */
const peerKey = ({ clientId, tab, user, focus }: BridgePeer) =>
  JSON.stringify([clientId, tab, user.id, user.name, user.avatar, focus?.field, focus?.label]);

const samePeers = (a: BridgePeer[], b: BridgePeer[]) =>
  a.length === b.length && a.every((p, i) => b[i] !== undefined && peerKey(p) === peerKey(b[i]));

const INERT_ENTITY: HydraEntityPresence = {
  peers: () => [],
  subscribePeers: () => () => {},
  setFocus: () => {},
  lock: async () => ({ acquired: false, holder: null }),
  takeOver: async () => ({ acquired: false, holder: null }),
  release: async () => {},
  locks: () => new Map(),
  subscribeLocks: () => () => {},
  leave: () => {},
};

type BridgeOptions = ActiveRealtime & { queryClient: QueryClient };

/** Builds the bridge for one realtime connection. */
export function createRealtimeBridge({
  transport,
  environmentId,
  tab,
  queryClient,
}: BridgeOptions): HydraRealtimeBridge {
  const avatarWaiters = new Set<() => void>();
  const avatars = createAvatarSource(queryClient, () => avatarWaiters.forEach((wake) => wake()));

  return {
    version: 1,
    selfTab: tab,
    colorForTab: (other) => {
      const { pointer, label } = colorForTab(other);
      return { pointer: resolveCssValue(pointer), label: resolveCssValue(label) };
    },
    joinEntity: (type, id) => {
      const channel = entityChannel(environmentId, type, id);
      if (channel === null) return INERT_ENTITY;
      const presence = createEntityPresence(transport, channel);
      const selfClient = () => transport.clientId();
      let lastPeers: BridgePeer[] = [];
      let lastLocks: BridgeLocks = new Map();
      // The same array until something a listener can see changes.
      const peers = () => {
        const list = presence.store.getList();
        avatars.ensure(list.map((peer) => peer.user.id));
        const next = list.map((peer) => toBridgePeer(peer, avatars));
        if (!samePeers(lastPeers, next)) lastPeers = next;
        return lastPeers;
      };
      const locks = () => {
        const next = toBridgeLocks(presence.locks.getTable(), selfClient(), avatars);
        if (!sameLocks(lastLocks, next)) lastLocks = next;
        return lastLocks;
      };
      // Tells `listener` when `read()` returns something new, whether the source or an avatar changed.
      const watch = <T>(read: () => T, listener: (value: T) => void, subscribe: (check: () => void) => () => void) => {
        let before = read();
        const check = () => {
          const next = read();
          if (next === before) return;
          before = next;
          listener(next);
        };
        avatarWaiters.add(check);
        const unsubscribe = subscribe(check);
        return () => {
          avatarWaiters.delete(check);
          unsubscribe();
        };
      };
      return {
        peers,
        subscribePeers: (listener) => watch(peers, listener, presence.store.subscribe),
        setFocus: presence.setFocus,
        lock: async (field) => toLockResult(await presence.locks.lock(field), selfClient(), avatars),
        takeOver: async (field) => toLockResult(await presence.locks.takeOver(field), selfClient(), avatars),
        release: presence.locks.release,
        locks,
        subscribeLocks: (listener) => watch(locks, listener, presence.locks.subscribe),
        leave: presence.release,
      };
    },
  };
}
